import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSessionStatus, extendSession } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function SessionTimer() {
  const { user, logout } = useAuth();
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [isExtending, setIsExtending] = useState(false);

  const WARNING_THRESHOLD_SECONDS = 120; // 2 minutes
  const SESSION_DURATION_MINUTES = 14;

  const checkSession = useCallback(async () => {
    if (!user) return;
    
    try {
      const status = await getSessionStatus();
      setRemainingSeconds(status.remaining_seconds);
      
      if (status.remaining_seconds <= WARNING_THRESHOLD_SECONDS && status.remaining_seconds > 0) {
        setShowWarning(true);
      }
      
      if (status.is_expired || status.remaining_seconds <= 0) {
        toast.error("Session expired. Please sign in again.");
        logout();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
    }
  }, [user, logout]);

  useEffect(() => {
    if (!user) return;
    
    // Initial check
    checkSession();
    
    // Update every second
    const timer = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev === null) return null;
        if (prev <= 1) {
          toast.error("Session expired. Please sign in again.");
          logout();
          return 0;
        }
        
        // Show warning at threshold
        if (prev === WARNING_THRESHOLD_SECONDS) {
          setShowWarning(true);
        }
        
        return prev - 1;
      });
    }, 1000);
    
    // Sync with server every 30 seconds
    const syncTimer = setInterval(checkSession, 30000);
    
    return () => {
      clearInterval(timer);
      clearInterval(syncTimer);
    };
  }, [user, logout, checkSession]);

  const handleExtendSession = async () => {
    setIsExtending(true);
    try {
      const response = await extendSession();
      
      // Update token in localStorage
      localStorage.setItem("hospice_token", response.token);
      
      // Reset timer
      setRemainingSeconds(SESSION_DURATION_MINUTES * 60);
      setShowWarning(false);
      
      toast.success("Session extended successfully");
    } catch (error) {
      toast.error("Failed to extend session. Please sign in again.");
      logout();
    } finally {
      setIsExtending(false);
    }
  };

  const formatTime = (seconds) => {
    if (seconds === null) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!user || remainingSeconds === null) return null;

  const isLow = remainingSeconds <= WARNING_THRESHOLD_SECONDS;

  return (
    <>
      {/* Session Timer Badge */}
      <div 
        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
          isLow 
            ? 'bg-red-100 text-red-700 animate-pulse' 
            : 'bg-primary/10 text-primary'
        }`}
        data-testid="session-timer"
      >
        <Clock className="w-3 h-3" />
        <span>{formatTime(remainingSeconds)}</span>
      </div>

      {/* Warning Dialog */}
      <Dialog open={showWarning} onOpenChange={setShowWarning}>
        <DialogContent className="sm:max-w-md" data-testid="session-warning-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <DialogTitle className="text-lg">Session Expiring Soon</DialogTitle>
                <DialogDescription>
                  Your session will expire in {formatTime(remainingSeconds)}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                For your security, sessions automatically expire after {SESSION_DURATION_MINUTES} minutes. 
                Click below to continue your session.
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-600 mb-2" data-testid="countdown-display">
                {formatTime(remainingSeconds)}
              </div>
              <p className="text-xs text-muted-foreground">Time remaining</p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowWarning(false);
                  logout();
                }}
                data-testid="logout-now-btn"
              >
                Sign Out
              </Button>
              <Button 
                className="flex-1"
                onClick={handleExtendSession}
                disabled={isExtending}
                data-testid="extend-session-btn"
              >
                {isExtending ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Extend Session
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
